import { useState, useEffect, useCallback } from 'react';

/**
 * Enhanced custom hook that returns whether the current viewport matches the provided media query
 * Optimized for mobile performance and reliability
 * @param {string} query - CSS media query string e.g. "(max-width: 640px)"
 * @returns {boolean} Whether the media query matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    // Safe server-side rendering default
    if (typeof window === 'undefined') return false;

    // Initialize with current match state
    try {
      return window.matchMedia(query).matches;
    } catch (error) {
      console.warn('Media query error:', error);
      return false;
    }
  });

  const updateMatches = useCallback((e) => {
    setMatches(e.matches);
  }, []);

  useEffect(() => {
    // Bail early if not in browser
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    let media;
    try {
      media = window.matchMedia(query);
    } catch (error) {
      console.warn('Invalid media query:', query);
      return;
    }

    // Set initial value if different (prevents unnecessary re-renders)
    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    // Use modern addEventListener if available
    if (media.addEventListener) {
      media.addEventListener("change", updateMatches);
      return () => media.removeEventListener("change", updateMatches);
    } 
    // Fallback for older browsers
    else if (media.addListener) {
      media.addListener(updateMatches);
      return () => media.removeListener(updateMatches);
    }
  }, [query, matches, updateMatches]);

  return matches;
} 